import { Controller } from "@hotwired/stimulus"
import { createAlert } from '../scripts/createAlert';
import WaveSurfer from 'wavesurfer.js'
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js'

let wavesurfer;
let scrollingWaveform = true;

export default class extends Controller {
  static targets = [
    'input',
    'recordButton',
    'stopButton',
    'removeButton',
    'transcribingNotice',
    'playButton',
    'pauseButton',
    'waveFormContainer',
    'audioFile',
    'questionIdInput'
  ]
  static values = {
    story: Number,
    interview: Number,
    question: Number,
    interviewTemplateId: Number,
    addAudioUrl: String,
    removeAudioUrl: String
  }

  connect() {
    // console.log("Hello, Story Admin Question Recorder", this.element)
    // console.log(this.storyValue, this.interviewValue, this.questionValue);
    // console.log(this.availabilityTargets)
    // console.log(this.requestIdValue)
    this.record = null;

    this.initAudio();
  }

  getCsrfToken() {
    const tokenEl = document.querySelector('meta[name="csrf-token"]')
    return tokenEl?.getAttribute('content')
  }

  getCurrentQuestionId() {
    if (this.hasQuestionIdInputTarget && this.questionIdInputTarget.value) {
      const parsed = parseInt(this.questionIdInputTarget.value, 10)
      return Number.isNaN(parsed) ? null : parsed
    }

    if (this.element?.dataset?.attachmentId) {
      const parsed = parseInt(this.element.dataset.attachmentId, 10)
      return Number.isNaN(parsed) ? null : parsed
    }

    if (this.hasQuestionValue && this.questionValue) return this.questionValue

    return null
  }

  getCreateQuestionUrl() {
    // Feature story flow: /stories/:story_id/interviews/:interview_id/questions
    if (this.hasStoryValue && this.hasInterviewValue && this.storyValue && this.interviewValue) {
      return `/stories/${this.storyValue}/interviews/${this.interviewValue}/questions`
    }

    // Interview template flow: /interview_templates/:interview_template_id/questions
    if (this.hasInterviewTemplateIdValue && this.interviewTemplateIdValue) {
      return `/interview_templates/${this.interviewTemplateIdValue}/questions`
    }

    return null
  }

  getQuestionParamKey() {
    // Must match the controller strong params keys for JSON creates.
    if (this.hasInterviewTemplateIdValue && this.interviewTemplateIdValue) {
      return 'stories_interview_template_question'
    }

    return 'stories_question'
  }

  getAddAudioUrl() {
    const questionId = this.getCurrentQuestionId()
    if (!questionId) return null

    if (this.hasAddAudioUrlValue && this.addAudioUrlValue) {
      return this.addAudioUrlValue
    }

    // Interview template flow when addAudioUrl isn't provided.
    if (this.hasInterviewTemplateIdValue && this.interviewTemplateIdValue) {
      return `/interview_templates/${this.interviewTemplateIdValue}/questions/${questionId}/add_audio`
    }

    // Feature story fallback.
    return `/stories/${this.storyValue}/interviews/${this.interviewValue}/questions/${questionId}/add_audio`
  }

  getRemoveAudioUrl() {
    const questionId = this.getCurrentQuestionId()
    if (!questionId) return null

    if (this.hasRemoveAudioUrlValue && this.removeAudioUrlValue) {
      return this.removeAudioUrlValue
    }

    if (this.hasInterviewTemplateIdValue && this.interviewTemplateIdValue) {
      return `/interview_templates/${this.interviewTemplateIdValue}/questions/${questionId}/remove_audio`
    }

    return `/stories/${this.storyValue}/interviews/${this.interviewValue}/questions/${questionId}/remove_audio`
  }

  getTargetLength() {
    const select = this.element.querySelector('select[name*="[target_length]"]')
    return select?.value
  }

  getEnabledState(fieldClass) {
    const input = this.element.querySelector(`.${fieldClass}`)
    return input?.checked || false
  }

  getRequiredState() {
    const requiredInput = this.element.querySelector('input[name*="[required]"]')
    return requiredInput?.checked || false
  }

  setQuestionId(questionId) {
    this.element.dataset.attachmentId = questionId

    if (this.hasQuestionIdInputTarget) {
      this.questionIdInputTarget.value = questionId
    }

    // Keep Stimulus "values" accessor in sync for any existing usages.
    this.questionValue = Number(questionId)
  }

  maybeReorderAfterCreate() {
    // Avoid ordering flicker + unnecessary requests for single-question lists.
    const controllerRoot = this.element.closest('[data-controller="stories-interview"], [data-controller="stories-interview-template-questions"]')
    if (!controllerRoot) return

    const cards = controllerRoot.querySelectorAll('.question-sortable')
    if (!cards || cards.length <= 1) return

    const orderedIds = Array.from(cards)
      .map((elm) => elm.dataset?.attachmentId)
      .filter((id) => id !== undefined && id !== null && String(id).trim() !== '')

    if (orderedIds.length === 0) return

    const csrfToken = this.getCsrfToken()

    // Feature story reorder endpoint: /stories/:story_id/interviews/:interview_id/questions/reorder
    if (this.hasStoryValue && this.hasInterviewValue && this.storyValue && this.interviewValue) {
      const reorderUrl = `/stories/${this.storyValue}/interviews/${this.interviewValue}/questions/reorder`
      const formBody = new URLSearchParams()
      orderedIds.forEach((id) => formBody.append('ordered_question_ids[]', id))

      fetch(reorderUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: formBody
      }).catch(() => { /* best-effort; recording shouldn't be blocked */ })
      return
    }

    // Interview template reorder endpoint: /interview_templates/:interview_template_id/reorder
    if (this.hasInterviewTemplateIdValue && this.interviewTemplateIdValue) {
      const reorderUrl = `/interview_templates/${this.interviewTemplateIdValue}/reorder`
      const formBody = new URLSearchParams()
      orderedIds.forEach((id) => formBody.append('ordered_question_ids[]', id))

      fetch(reorderUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: formBody
      }).catch(() => { /* best-effort; recording shouldn't be blocked */ })
    }
  }

  async ensureQuestionExists() {
    const currentId = this.getCurrentQuestionId()
    if (currentId) return currentId

    const createUrl = this.getCreateQuestionUrl()
    if (!createUrl) throw new Error('Unable to determine question create URL')

    const questionParamKey = this.getQuestionParamKey()
    const csrfToken = this.getCsrfToken()

    const questionText = this.hasInputTarget ? (this.inputTarget.value || '') : ''

    // Capture current UI state so the auto-created record stays in sync even before parent "Save".
    const formData = new FormData()
    formData.append(`${questionParamKey}[text]`, questionText)
    formData.append(`${questionParamKey}[audio_enabled]`, this.getEnabledState('audio-response') ? '1' : '0')
    formData.append(`${questionParamKey}[text_enabled]`, this.getEnabledState('text-response') ? '1' : '0')
    formData.append(`${questionParamKey}[video_enabled]`, this.getEnabledState('video-response') ? '1' : '0')
    formData.append(`${questionParamKey}[required]`, this.getRequiredState() ? '1' : '0')

    const targetLength = this.getTargetLength()
    if (targetLength) {
      formData.append(`${questionParamKey}[target_length]`, targetLength)
    }

    const response = await fetch(createUrl, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: formData
    })

    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`Question auto-create failed: ${response.status} ${text}`)
    }

    const data = await response.json()
    const newId = data?.id
    if (!newId) throw new Error('Question auto-create returned no id')

    this.setQuestionId(newId)
    this.maybeReorderAfterCreate()

    return newId
  }

  initAudio() {
    //check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Your browser does not support audio recording!');
      return;
    }

    this.createWaveSurfer();
  }

  createWaveSurfer() {
    // Create an instance of WaveSurfer
    if (wavesurfer) {
      wavesurfer.destroy()
    }

    wavesurfer = WaveSurfer.create({
      container: this.waveFormContainerTarget,
      waveColor: '#0e1a2c',
      progressColor: '#FFD81B',
      height: 1, // we aren't display the waveform
      // Set a bar width
      barWidth: 2,
      // Optionally, specify the spacing between bars
      barGap: 1,
      // And the bar radius
      barRadius: 2
    })

    // Initialize the Record plugin
    this.record = wavesurfer.registerPlugin(RecordPlugin.create({ scrollingWaveform, renderRecordedAudio: false }))
    // Render recorded audio
    this.record.on('record-end', async (blob) => {

      // remove the old audio file if its present
      if (this.hasAudioFileTarget) {
        this.audioFileTarget.remove();
      }

      // create file...
      let file = new File([blob], "audio-question.wav", { lastModified: new Date().getTime() });

      // ... to post to the server
      const formData = new FormData();
      formData.append('audioFile', file);

      const addUrl = this.getAddAudioUrl()
      if (!addUrl) {
        console.log('Missing add_audio URL; question id may be missing.')
        return
      }

      fetch(addUrl, {
        method: 'post',
        body: formData,
        accept: 'application/json'
      })
      .then(response => response.json())
      .then(data =>{
        // ... and attach to the DOM
        const audioElement = document.createElement('audio');
        audioElement.src = URL.createObjectURL(file);
        audioElement.controls = false;
        audioElement.dataset['storyAdminQuestionRecorderTarget'] = 'audioFile';
        audioElement.classList.add('d-none');
        this.element.appendChild(audioElement);

        // hide the transcribing notice
        this.transcribingNoticeTarget.classList.remove('d-block');
        this.transcribingNoticeTarget.classList.add('d-none');

        // display the remove button
        this.removeButtonTarget.classList.remove('d-none');
        this.removeButtonTarget.classList.add('d-block');

        this.playButtonTarget.classList.remove('d-none');
        this.playButtonTarget.classList.add('d-block');

        this.inputTarget.classList.add('ps-5');
        this.inputTarget.value = data.text;

        createAlert('success', 'Audio file uploaded successfully')
      }).catch(error => {
        console.log("Error uploading audio", error);
      });

    })

  }

  focusInput() {
    this.inputTarget.focus()
  }

  async recordAudio() {
    // Ensure the question exists so we have an id for add_audio/upload.
    await this.ensureQuestionExists()

    this.record.startRecording().then(() => {
      // hide the record button
      this.recordButtonTarget.classList.remove('d-block');
      this.recordButtonTarget.classList.add('d-none');

      // display the stop button
      this.stopButtonTarget.classList.remove('d-none');
      this.stopButtonTarget.classList.add('d-block');
    })
  }

  stopAudio() {
    if (this.record.isRecording() || this.record.isPaused()) {
      this.record.stopRecording()
    }

    // hide the stop button
    this.stopButtonTarget.classList.remove('d-block');
    this.stopButtonTarget.classList.add('d-none');

    // display the transcribing notice
    this.transcribingNoticeTarget.classList.remove('d-none');
    this.transcribingNoticeTarget.classList.add('d-block');
  }

  removeAudio() {
    const removeUrl = this.getRemoveAudioUrl()
    if (!removeUrl) {
      console.log('Missing remove_audio URL; question id may be missing.')
      return
    }

    fetch(removeUrl, {
      method: 'delete',
      accept: 'application/json'
    }).then( () => {
      // hide the remove button
      this.removeButtonTarget.classList.remove('d-block');
      this.removeButtonTarget.classList.add('d-none');

      // remove the play button
      this.playButtonTarget.classList.remove('d-block');
      this.playButtonTarget.classList.add('d-none');

      // remove the pause button
      this.pauseButtonTarget.classList.remove('d-block');
      this.pauseButtonTarget.classList.add('d-none');

      // display the record button
      this.recordButtonTarget.classList.remove('d-none');
      this.recordButtonTarget.classList.add('d-block');

      // remove the audio file
      this.audioFileTarget.remove();

      this.inputTarget.classList.remove('ps-5')

      createAlert('success', 'Audio file removed')
    }).catch(error => {
      console.log("Error removing audio", error);
    });

  }

  playAudio() {
    this.playButtonTarget.classList.remove('d-block');
    this.playButtonTarget.classList.add('d-none');

    this.pauseButtonTarget.classList.remove('d-none');
    this.pauseButtonTarget.classList.add('d-block');

    this.audioFileTarget.addEventListener('ended', () => {
      this.pauseButtonTarget.classList.remove('d-block');
      this.pauseButtonTarget.classList.add('d-none');

      this.playButtonTarget.classList.remove('d-none');
      this.playButtonTarget.classList.add('d-block');
    });

    this.audioFileTarget.play();
  }

  pauseAudio() {
    this.pauseButtonTarget.classList.remove('d-block');
    this.pauseButtonTarget.classList.add('d-none');

    this.playButtonTarget.classList.remove('d-none');
    this.playButtonTarget.classList.add('d-block');

    this.audioFileTarget.pause();
  }

}
